/* Homepage-only enhancements. The unenhanced Liquid page remains fully usable. */
(() => {
  if (customElements.get('anonym-motion-home')) return;
  let libraries;
  const loadScript = (url) => new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = url;
    script.onload = resolve;
    script.onerror = reject;
    document.head.append(script);
  });
  function loadAnimationLibraries() {
    if (!libraries) libraries = (async () => {
      if (!window.gsap) await loadScript('https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/gsap.min.js');
      if (!window.ScrollTrigger) await loadScript('https://cdn.jsdelivr.net/npm/gsap@3.12.5/dist/ScrollTrigger.min.js');
      window.gsap.registerPlugin(window.ScrollTrigger);
    })();
    return libraries;
  }
  class AnonymMotionHome extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const menu = this.querySelector('.am-menu');
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && menu.open) { menu.open = false; menu.querySelector('summary').focus(); }
      }, { signal });
      this.addEventListener('click', (event) => {
        if (event.target.closest('.am-menu-panel a')) menu.open = false;
      }, { signal });
      document.addEventListener('click', (event) => {
        if (!menu.contains(event.target)) menu.open = false;
      }, { signal });
      if (this.dataset.animate === 'true') this.enhance(signal);
    }
    async enhance(signal) {
      try {
        await loadAnimationLibraries();
        if (document.fonts) await document.fonts.ready;
        if (signal.aborted || !this.isConnected) return;
        const gsap = window.gsap;
        this.media = gsap.matchMedia();
        this.media.add({
          desktop: '(min-width: 769px) and (min-height: 650px)',
          motion: '(prefers-reduced-motion: no-preference)'
        }, (context) => {
          if (!context.conditions.motion) return;
          const compact = !context.conditions.desktop;
          this.classList.add('am-animated');
          this.classList.toggle('am-mobile-motion', compact);
          const hero = this.querySelector('.am-hero');
          const viewportHeight = () => this.querySelector('.am-hero-viewport').clientHeight;
          const heroEnd = () => `top+=${Math.max(1,hero.offsetHeight-viewportHeight())} top`;
          const header = this.querySelector('.am-header');
          const videos = [...this.querySelectorAll('.am-hero-video, .am-reveal-video')];
          const states = videos.map(video => ({ video, region:video.closest('.am-hero-viewport, .am-reveal'), target:0, time:video.currentTime || 0, visible:!compact, playing:false, unlocked:false, lastUnlockAttempt:0, unlockTimer:0, originalLoop:video.loop }));
          const heroState = states.find(state => state.video.classList.contains('am-hero-video'));
          const revealState = states.find(state => state.video.classList.contains('am-reveal-video'));
          videos.forEach(video => {
            video.pause();
            // Select the working mobile encode once, before any scroll seeks.
            // Safari and desktop retain their existing source selection.
            const chromeSource = video.dataset?.iosChromeSrc;
            if (compact && typeof navigator !== 'undefined' && /CriOS\//.test(navigator.userAgent)
                && window.matchMedia('(max-width: 768px)').matches && chromeSource
                && video.getAttribute('src') !== chromeSource) {
              video.src = chromeSource;
              video.load();
            }
            // Keep Shopify's selected source and decoded frame intact. Replacing
            // src/load on touch can reset iOS video back to its poster.
            video.preload = 'metadata';
            video.muted = true;
            video.playsInline = true;
          });
          let frame = 0;
          let stopped = false;
          const tick = () => {
            frame = 0;
            if (stopped || document.hidden) return;
            let pending = false;
            states.forEach(state => {
              const {video} = state;
              if (state.playing || (compact && !state.visible)) return;
              if (!Number.isFinite(video.duration) || video.readyState < 1 || video.error) return;
              const desired = state.target * Math.max(0, video.duration - 0.05);
              const threshold = compact ? 0.06 : 0.01;
              if (video.seeking) return; // Resume on seeked, not a busy animation loop.
              // Phones decode only the newest requested position, without a
              // backlog of intermediate seeks. Both directions remain scrubbable.
              state.time = compact ? desired : state.time + (desired - state.time) * 0.08;
              if (Math.abs(desired - state.time) < threshold) state.time = desired;
              if (Math.abs(video.currentTime - state.time) > threshold) {
                video.currentTime = state.time;
              }
              if (!video.seeking && Math.abs(desired - state.time) > threshold) pending = true;
            });
            if (pending) frame = requestAnimationFrame(tick);
          };
          const wake = () => { if (!frame && !stopped) frame = requestAnimationFrame(tick); };
          const mediaEvents = ['loadedmetadata', 'loadeddata', 'canplay', 'seeked'];
          videos.forEach(video => mediaEvents.forEach(event => video.addEventListener(event, wake)));
          const visibilityObserver = compact ? new IntersectionObserver(entries => {
            entries.forEach(entry => {
              const state = states.find(item => item.region === entry.target);
              if (!state) return;
              state.visible = entry.isIntersecting;
              if (state.visible) {
                state.video.preload = 'auto';
              } else {
                state.video.pause();
              }
            });
            wake();
          }, {threshold:0}) : null;
          states.forEach(state => { if (state.region) visibilityObserver?.observe(state.region); });
          // Unlock mobile decoding once, then return to paused scroll control.
          const primeMedia = () => {
            if (!compact || stopped) return;
            states.forEach(state => {
              const bounds = state.region?.getBoundingClientRect();
              state.visible = Boolean(bounds && bounds.bottom > 0 && bounds.top < window.innerHeight);
              // Keep offscreen decoders idle: priming both videos together can
              // interrupt the hero on iOS. Re-check visibility as touch moves.
              if (!state.visible || state.unlocked || state.playing || state.video.error) return;
              const now = Date.now();
              if (state.lastUnlockAttempt && now - state.lastUnlockAttempt < 3000) return;
              state.lastUnlockAttempt = now;
              state.playing = true;
              state.video.preload = 'auto';
              let released = false;
              const release = () => {
                if (released) return;
                released = true;
                clearTimeout(state.unlockTimer);
                state.unlockTimer = 0;
                state.video.pause();
                state.playing = false;
                if (!stopped) wake();
              };
              // iOS may leave play() pending while scrolling. Never allow that
              // promise to block all subsequent scroll-driven seeks indefinitely.
              state.unlockTimer = setTimeout(release, 500);
              Promise.resolve(state.video.play()).then(() => {
                if (!released) state.unlocked = true;
              }).catch(() => {
                // Retry on the next touch if the browser blocks playback.
              }).finally(release);
            });
          };
          this.addEventListener('pointerdown', primeMedia, {passive:true});
          this.addEventListener('touchstart', primeMedia, {passive:true});
          this.addEventListener('touchmove', primeMedia, {passive:true});
          this.addEventListener('touchend', primeMedia, {passive:true});
          document.addEventListener('visibilitychange', wake);
          window.ScrollTrigger.create({ trigger:hero, start:'top top', end:heroEnd, onUpdate:self => {
            if (heroState) { heroState.target = self.progress; wake(); }
          }});
          const splitLines = [...this.querySelectorAll('[data-split]')];
          splitLines.forEach(line => {
            const text = line.textContent;
            line.dataset.original = text;
            line.setAttribute('aria-label', text);
            line.replaceChildren(...Array.from(text, char => {
              const span = document.createElement('span');
              span.className = 'am-char'; span.textContent = char; span.setAttribute('aria-hidden','true'); return span;
            }));
          });
          const exitStart = () => `top+=${(hero.offsetHeight-viewportHeight())*0.8} top`;
          gsap.to(this.querySelectorAll('.am-char'), { opacity:0, y:-24, filter:compact ? 'none' : 'blur(8px)', stagger:0.02, ease:'none', scrollTrigger:{trigger:hero, start:exitStart, end:heroEnd, scrub:true} });
          gsap.to(this.querySelector('.am-hero-copy .am-pill'), {opacity:0, y:-20, scrollTrigger:{trigger:hero,start:exitStart,end:heroEnd,scrub:true}});
          const products = this.querySelector('.am-products');
          const track = this.querySelector('.am-product-track');
          const reveal = this.querySelector('.am-reveal');
          if (!compact && track.children.length > 1) {
            this.classList.add('am-carousel-animated');
            const overflow = () => Math.max(0, track.scrollWidth - track.clientWidth);
            const timeline = gsap.timeline({scrollTrigger:{trigger:products,start:'top top',end:()=>`+=${overflow() + (reveal ? window.innerHeight : 0) + 1}`,pin:true,scrub:0.6,invalidateOnRefresh:true}});
            timeline.to(track,{x:()=>-overflow(),ease:'none',duration:3});
            if (reveal) timeline.to(reveal, {clipPath:'inset(0 0%)',duration:1.5,ease:'none',onUpdate:()=> {
              if (revealState) { revealState.target = Math.max(0,(timeline.progress()-2/3)*3); wake(); }
            }});
            // Keyboard focus must bring an offscreen product back into view.
            const focusProduct = (event) => {
              const card = event.target.closest('.am-product-card');
              const trigger = timeline.scrollTrigger;
              if (!card || !trigger) return;
              const offset = Math.min(overflow(), card.offsetLeft);
              const fraction = overflow() ? offset / overflow() : 0;
              window.scrollTo({top:trigger.start + fraction * (trigger.end-trigger.start) * (reveal ? 2/3 : 1),behavior:'instant'});
            };
            track.addEventListener('focusin',focusProduct);
            this.removeFocus = () => track.removeEventListener('focusin',focusProduct);
          }
          if (compact && reveal) {
            gsap.fromTo(reveal, {clipPath:'inset(0 35%)'}, {clipPath:'inset(0 0%)',ease:'none',scrollTrigger:{trigger:reveal,start:'top 90%',end:'top 25%',scrub:0.4}});
            window.ScrollTrigger.create({trigger:reveal,start:'top bottom',end:'bottom top',onUpdate:self => {
              if (revealState) { revealState.target = self.progress; wake(); }
            }});
          }
          this.querySelectorAll('[data-fade]').forEach(element => gsap.from(element,{autoAlpha:0,y:20,duration:0.8,scrollTrigger:{trigger:element,start:'top 92%',toggleActions:'play none none reverse'}}));
          const footer = this.querySelector('.am-footer');
          const fitFooter = () => {
            this.classList.toggle('am-footer-reveal',!compact && footer.offsetHeight < window.innerHeight * 0.85);
            header.classList.toggle('am-fixed', !compact && window.innerHeight > 600);
          };
          const resize = new ResizeObserver(fitFooter); resize.observe(footer); fitFooter();
          window.addEventListener('resize',fitFooter);
          window.ScrollTrigger.refresh();
          wake();
          return () => {
            stopped = true; cancelAnimationFrame(frame);
            visibilityObserver?.disconnect();
            videos.forEach(video => mediaEvents.forEach(event => video.removeEventListener(event,wake)));
            states.forEach(state => { clearTimeout(state.unlockTimer); state.video.pause(); state.video.loop = state.originalLoop; });
            this.removeEventListener('pointerdown',primeMedia);
            this.removeEventListener('touchstart',primeMedia);
            this.removeEventListener('touchmove',primeMedia);
            this.removeEventListener('touchend',primeMedia);
            document.removeEventListener('visibilitychange',wake);
            window.removeEventListener('resize',fitFooter);
            resize.disconnect(); this.removeFocus?.();
            this.classList.remove('am-animated','am-mobile-motion','am-carousel-animated','am-footer-reveal');
            header.classList.remove('am-fixed');
            splitLines.forEach(line => {line.textContent=line.dataset.original;line.removeAttribute('aria-label');});
          };
        }, this);
      } catch (error) {
        // A blocked CDN must never prevent shopping or reading the page.
        console.warn('ANONYM: scroll effects unavailable; using the standard layout.', error);
        this.media?.revert();
      }
    }
    disconnectedCallback() { this.controller?.abort(); this.media?.revert(); }
  }
  customElements.define('anonym-motion-home', AnonymMotionHome);
})();

